import { useCallback, useEffect, useState } from "react";
import { phoneNumberRegex } from "shared/utils";
import { z } from "zod";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { useFormHandler } from "shared/hooks";
import { useBookerSendContactUsEmailLazyQuery } from "gql/generated";
import i18n, { i18nLanguage, i18nNamespace, loadLanguagePacks } from "i18n";
import { useTranslation } from "react-i18next";

const getContactUsFormSchema = () =>
  z.object({
    firstName: z
      .string({
        required_error: `${i18n.t("booker:CONTACT_PAGE.FORM.FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .min(1, {
        message: `${i18n.t("booker:CONTACT_PAGE.FORM.FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      }),
    lastName: z
      .string({
        required_error: `${i18n.t("booker:CONTACT_PAGE.FORM.LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .min(1, {
        message: `${i18n.t("booker:CONTACT_PAGE.FORM.LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      }),
    email: z
      .string({
        required_error: `${i18n.t("booker:CONTACT_PAGE.FORM.EMAIL")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .min(1, {
        message: `${i18n.t("booker:CONTACT_PAGE.FORM.EMAIL")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .email({
        message: i18n.t("core:FORM_ERRORS.INVALID_EMAIL"),
      }),
    phone: z
      .string({
        required_error: `${i18n.t("booker:CONTACT_PAGE.FORM.PHONE")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .min(1, {
        message: `${i18n.t("booker:CONTACT_PAGE.FORM.PHONE")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .regex(phoneNumberRegex, {
        message: `${i18n.t("core:FORM_ERRORS.INVALID")} ${i18n.t("booker:CONTACT_PAGE.FORM.PHONE")}`,
      }),
    message: z
      .string({
        required_error: `${i18n.t("booker:CONTACT_PAGE.FORM.MESSAGE")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .min(1, {
        message: `${i18n.t("booker:CONTACT_PAGE.FORM.MESSAGE")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      }),
  });

export type ContactUsFormValues = z.infer<
  ReturnType<typeof getContactUsFormSchema>
>;

interface ContactUsFormOptions {
  onSuccess?: (v: ContactUsFormValues | undefined) => void;
  onError?: (message: string) => void;
}

export const useContactUsForm = ({
  onSuccess,
  onError,
}: ContactUsFormOptions) => {
  const { i18n } = useTranslation();
  const formErrorMessage = i18n.t("booker:CONTACT_PAGE.FORM.ERROR");

  const [sendContactUsEmail, { loading }] =
    useBookerSendContactUsEmailLazyQuery();
  const { booker } = useBookerContext();
  const { customer } = useCartContext();

  const defaultValues: ContactUsFormValues = {
    email: customer.email || "",
    message: "",
    firstName: customer.firstName || "",
    lastName: customer.lastName || "",
    phone: customer.phone || "",
  };

  /**
   * Instead of using a const schema, we need to use a state variable
   * because the schema is dynamically updated when the language changes
   * - This will load the "en" schema initially, and then if the language
   *   changes, will load the schema of the new language
   */
  const [schema, setSchema] = useState<
    ReturnType<typeof getContactUsFormSchema>
  >(getContactUsFormSchema());

  /**
   * Dynamically update the schema when the language changes
   * - When the language is changed, we need to reload the schema to update the
   *   error messages to the new language
   */
  useEffect(() => {
    const loadLanguagePacksAndUpdateSchemas = async () => {
      await loadLanguagePacks(
        [i18nNamespace.Core, i18nNamespace.Booker],
        "booker",
        i18n.language as i18nLanguage,
      );
      setSchema(getContactUsFormSchema());
    };
    loadLanguagePacksAndUpdateSchemas();
  }, [i18n.language, setSchema]);

  const formHandler = useFormHandler<ContactUsFormValues>(
    useCallback(
      async data => {
        const payload = { ...data };
        const { email, message, firstName, lastName, phone } = payload;

        if (email && message && firstName && lastName && phone) {
          const { data: res, error } = await sendContactUsEmail({
            variables: {
              details: { email, firstName, lastName, phone, message },
              bookerId: booker?._id ?? "",
            },
          });

          if (res?.bookerSendContactUsEmail?.success) {
            onSuccess?.(data as ContactUsFormValues);
          } else if (error) {
            onError?.(formErrorMessage);
          } else {
            onError?.(formErrorMessage);
          }
        }
      },
      [sendContactUsEmail, booker?._id, onSuccess, onError, formErrorMessage],
    ),
    defaultValues,
    { schema },
  );

  return { ...formHandler, loading };
};
